import type { DashboardUser } from '@nono/contract';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { ApiError, api, setAuthToken } from './api';

/* ---- minimal Google Identity Services typings (no @types dependency) ---- */
interface GisIdConfig {
  client_id: string;
  callback: (resp: { credential: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  use_fedcm_for_prompt?: boolean;
}
interface GisButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'small' | 'medium' | 'large';
  shape?: 'rectangular' | 'pill';
  text?: 'signin_with' | 'continue_with';
  width?: number;
  locale?: string;
}
interface GisAccountsId {
  initialize(cfg: GisIdConfig): void;
  renderButton(parent: HTMLElement, opts: GisButtonOptions): void;
  prompt(): void;
  disableAutoSelect(): void;
}
declare global {
  interface Window {
    google?: { accounts: { id: GisAccountsId } };
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

export type AuthStatus =
  | 'loading' // fetching /config
  | 'unconfigured' // no Google client id on the server
  | 'unauthenticated' // no token yet
  | 'verifying' // token present, /me in flight
  | 'authenticated'
  | 'denied' // token valid but email not on the allowlist
  | 'error';

interface AuthContextValue {
  status: AuthStatus;
  user: DashboardUser | null;
  deniedEmail: string | null;
  googleClientId: string | null;
  gisReady: boolean;
  renderButton: (el: HTMLElement, locale: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadGisScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GIS load failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener('load', () => resolve());
    s.addEventListener('error', () => reject(new Error('GIS load failed')));
    document.head.appendChild(s);
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [gisReady, setGisReady] = useState(false);
  const queryClient = useQueryClient();

  // /config is public — tells us whether sign-in is configured + the client id.
  const configQuery = useQuery({
    queryKey: ['config'],
    queryFn: () => api.config(),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const googleClientId = configQuery.data?.googleClientId ?? null;

  // /me runs immediately: returning visitors authenticate via the HttpOnly
  // session cookie alone (no Google round-trip, no popup). 401 = sign in.
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    enabled: configQuery.isSuccess && Boolean(googleClientId),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Google sign-in succeeded → use the credential as Bearer right away AND
  // exchange it for the 30-day session cookie ("sign in once, stay in").
  const applyToken = useCallback(
    (credential: string) => {
      setAuthToken(credential);
      setToken(credential);
      void api
        .createSession(credential)
        .catch(() => {
          /* cookie is an enhancement — Bearer still authenticates this tab */
        })
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: ['me'] });
        });
    },
    [queryClient],
  );

  // Load GIS + initialize once the client id is known.
  useEffect(() => {
    if (!googleClientId) return;
    let cancelled = false;
    loadGisScript()
      .then(() => {
        if (cancelled || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (resp) => applyToken(resp.credential),
          auto_select: true,
          cancel_on_tap_outside: false,
        });
        setGisReady(true);
      })
      .catch(() => setGisReady(false));
    return () => {
      cancelled = true;
    };
  }, [googleClientId, applyToken]);

  const renderButton = useCallback(
    (el: HTMLElement, locale: string) => {
      if (!gisReady || !window.google) return;
      el.replaceChildren();
      window.google.accounts.id.renderButton(el, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: 280,
        locale,
      });
    },
    [gisReady],
  );

  const signOut = useCallback(() => {
    void api.deleteSession().catch(() => {
      /* cookie may already be gone */
    });
    window.google?.accounts.id.disableAutoSelect();
    setAuthToken(null);
    setToken(null);
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [queryClient]);

  let status: AuthStatus;
  let deniedEmail: string | null = null;
  const devPreview = import.meta.env.DEV && Boolean(import.meta.env.VITE_ADMIN_TOKEN);
  if (devPreview) status = 'authenticated';
  else if (configQuery.isLoading) status = 'loading';
  else if (configQuery.isError) status = 'error';
  else if (!googleClientId) status = 'unconfigured';
  else if (meQuery.isSuccess) status = 'authenticated';
  else if (meQuery.isError) {
    const err = meQuery.error;
    if (err instanceof ApiError && err.status === 403) {
      status = 'denied';
      deniedEmail = err.message || null;
    } else {
      // 401 = no session cookie and no token → show the sign-in page.
      status = token === null ? 'unauthenticated' : 'verifying';
    }
  } else status = 'verifying';

  const user = meQuery.data?.user ?? null;

  return (
    <AuthContext.Provider
      value={{ status, user, deniedEmail, googleClientId, gisReady, renderButton, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
